import { chakra, type HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef, type ElementType, type ReactNode } from 'react';

const Base = chakra('div', { base: { minWidth: 0 } });

type SurfaceTone = 'default' | 'raised' | 'subtle' | 'inverse';
type Elevation = 'none' | 'xs' | 'sm' | 'md' | 'lg';
type SpaceToken =
  | 'none' | '1' | '2' | '3' | '4' | '5' | '6' | '8' | '10' | '12';
type RadiusToken = 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const toneStyle: Record<SurfaceTone, HTMLChakraProps<'div'>> = {
  default: { bg: 'surface.default', color: 'text.primary' },
  raised: { bg: 'surface.raised', color: 'text.primary' },
  subtle: { bg: 'surface.subtle', color: 'text.primary' },
  inverse: { bg: 'surface.inverse', color: 'text.inverse' },
};

export interface SurfaceProps extends Omit<React.HTMLAttributes<HTMLElement>, 'color'> {
  as?: ElementType;
  tone?: SurfaceTone;
  elevation?: Elevation;
  /** Uniform inner padding (spacing token). */
  padding?: SpaceToken;
  radius?: RadiusToken;
  /** Hairline border in the default border color. */
  bordered?: boolean;
  /** Clickable-card affordance: pointer cursor + hover lift (border + raised bg). */
  interactive?: boolean;
  /**
   * Status emphasis (#275): tint the surface + border so a row visibly stands out as
   * needing attention. `danger` flags a failing/blocked item (a subtle red fill + a
   * red-tinted border). Layered after `bordered`, so it overrides the border colour.
   */
  highlight?: 'danger';
  children?: ReactNode;
}

/** A themed container — the base building block for cards, panels, popovers, sheets. */
export const Surface = forwardRef<HTMLDivElement, SurfaceProps>(function Surface(
  { as, tone = 'default', elevation = 'none', padding, radius = 'lg', bordered, interactive, highlight, ...rest },
  ref,
) {
  const sx: HTMLChakraProps<'div'> = {
    ...toneStyle[tone],
    boxShadow: elevation === 'none' ? undefined : elevation,
    p: padding === 'none' ? '0' : padding,
    borderRadius: radius,
    ...(bordered ? { borderWidth: '1px', borderColor: 'border.default' } : {}),
    // Status emphasis (#275): a subtle tonal fill + a tinted border. Colour is never the
    // only cue — the row also carries a danger-toned health badge with a label + icon.
    ...(highlight === 'danger'
      ? { bg: 'status.dangerSubtle', borderWidth: '1px', borderColor: 'status.dangerFg/30' }
      : {}),
    ...(interactive
      ? {
          cursor: 'pointer',
          transitionProperty: 'border-color, background-color, box-shadow',
          transitionDuration: 'fast',
          transitionTimingFunction: 'standard',
          _hover: { borderColor: 'border.strong', bg: 'surface.raised' },
          _focusWithin: { borderColor: 'border.strong' },
        }
      : {}),
  };
  // Reflect the emphasis on the DOM (state hook for tests + styling); omitted when unset.
  return <Base as={as} ref={ref} data-highlight={highlight} {...sx} {...rest} />;
});
